import React, { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, Fab, Divider } from '@mui/material';
import MailIcon from '@mui/icons-material/Mail';
import InstagramIcon from '@mui/icons-material/Instagram';
import RoomIcon from '@mui/icons-material/Room';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import LanguageIcon from '@mui/icons-material/Language';
import FacebookIcon from '@mui/icons-material/Facebook';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import { SvgIcon } from '@mui/material';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { BookOnlineOutlined, LockClock } from '@mui/icons-material';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { baseURL } from 'utils/api';
import dummyImage from "assets/images/2665820.jpg";
import Loading from 'components/ApnaLoading';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';

// Styles
const styles = {
    container: {
        width: '365px',
        height: '764px',
        margin: '0 auto',
        background: '#000',
        position: 'relative',
        border: '12px solid #1a1a1a',
        borderRadius: "55px",
        padding: '0',
        boxShadow: '0 0 50px rgba(0, 0, 0, 0.3)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        // iPhone 15 frame styling
        '&::before': {
            content: '""',
            position: 'absolute',
            top: '10px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '110px', // Actual Dynamic Island width
            height: '31px',
            background: '#000',
            borderRadius: '50px',
            zIndex: 2
        },
        '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '8px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '134px', // Actual home indicator width
            height: '5px',
            background: '#000',
            borderRadius: '5px',
            zIndex: 2
        }
    },
    contentWrapper: {
        position: 'relative',
        zIndex: 1,
        background: 'white',
        borderRadius: '40px',
        overflow: 'hidden',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        margin: '2px',
        width: '100%',
        height: '100%'
    },
    scrollableContent: {
        flex: 1,
        overflowY: 'auto',
        width: '100%',
        height: '100%',
        '&::-webkit-scrollbar': {
            width: '0px',
        },
        '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb': {
            background: '#E68DF3',
            borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
            background: '#c21fad',
        }
    },
    headerImage: {
        height: '280px',
        width: '100%',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        position: 'relative',
        marginTop: '0',
    },
    socialIcons: {
        position: 'absolute',
        top: 100,
        right: 5,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '100%',
        gap: 0.1,
    },
    fab: {
        borderRadius: '0%',
        boxShadow: 0,
        background: 'linear-gradient(90deg, #AF27D2, #ED00FC)',
    },
    mainCard: {
        width: '100%',
        borderRadius: 3,
        mt: 2,
        height: 'auto',
        mx: 'auto',
        border: '2px solid transparent',
        background: 'linear-gradient(white, white) padding-box, linear-gradient(90deg,#3491EB, #E68DF3) border-box',
    },
    divider: {
        background: 'linear-gradient(90deg, #3491EB, #E68DF3)',
        width: '110%',
        height: '1px',
        ml: '-5%',
    },
    amenityIcon: {
        background: '#F8E0F9',
        borderRadius: '50%',
        padding: '10px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: '#333',
    }
};

// Sub-components
const HeaderImage = ({ image, onBack, onFavorite }) => {
    const matches = useMediaQuery('(max-width:600px)');

    return (
        <SoftBox sx={{
            ...styles.headerImage,
            backgroundImage: image ? `url(${image})` : `url(${dummyImage})`,
            height: '320px' // Increased height from default
        }}>
            <Box sx={{ padding: matches ? 0 : 2, position: 'absolute', top: 10, left: 8 }}>
                <KeyboardArrowLeftIcon sx={{ color: '#E68DF3', fontSize: 40, borderRadius: '50%', background: 'linear-gradient(90deg, #fff, #fff)' }} />
            </Box>
            <Box sx={{ padding: matches ? 0 : 2, position: 'absolute', top: 10, right: 10 }}>
                <FavoriteBorderIcon sx={{ color: '#E68DF3' }} />
            </Box>
            <SocialIcons />

            {/* Image Slider Dots */}
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 40, // Changed from top: 210 to bottom: 20 for better positioning
                    left: 15,
                    display: 'flex',
                    gap: 1,
                }}
            >
                <Box
                    sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: '#E68DF3',
                    }}
                />
                <Box
                    sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: 'linear-gradient(90deg, #E68DF3, #3491EB)',
                    }}
                />
                <Box
                    sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: '#E68DF3',
                    }}
                />
                <Box
                    sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: '#E68DF3',
                    }}
                />
                <Box
                    sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: '#E68DF3',
                    }}
                />
            </Box>
        </SoftBox>
    );
};

const SocialIcons = () => (
    <Box sx={styles.socialIcons}>
        <Fab size="small" sx={{ ...styles.fab, color: 'white' }}><LanguageIcon color="white" /></Fab>
        <Fab size="small" sx={{ ...styles.fab, color: 'white' }}><InstagramIcon color="white" /></Fab>
        <Fab size="small" sx={{ ...styles.fab, color: 'white' }}><FacebookIcon color="white" /></Fab>
    </Box>
);

const BusinessInfo = ({ businessName, rating, keywords }) => (
    <SoftBox display="flex" alignItems="center" justifyContent="space-between" >
        <SoftTypography
            variant="h1"
            fontWeight="bold"
            sx={{
                fontSize: '1.2rem',
                background: 'linear-gradient(90deg, #2399FA, #6F60D1)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
            }}
        >
            {businessName}
        </SoftTypography>
        <Box display="flex" alignItems="center">
            <StarBorderIcon sx={{ fontSize: '20px', color: '#E9A2F6' }} />
            <SoftTypography variant="caption" fontWeight="bold" sx={{ fontSize: '0.9rem', color: 'gray' }}>
                {rating}
            </SoftTypography>
        </Box>
    </SoftBox>
);

const ContactIcons = () => (
    <Box sx={{ display: 'flex', gap: 5, justifyContent: 'space-around', mt: "10px" }}>
        <SvgIcon
            component="svg"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            sx={{ fontSize: 60 }}
        >
            <defs>
                <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{ stopColor: 'skyblue', stopOpacity: 1 }} />
                    <stop offset="100%" style={{ stopColor: 'purple', stopOpacity: 1 }} />
                </linearGradient>
            </defs>
            <LocalPhoneOutlinedIcon sx={{ fill: 'url(#gradient1)', fontSize: 'inherit' }} />
        </SvgIcon>

        <SvgIcon
            component="svg"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            sx={{ fontSize: 60 }}
        >
            <defs>
                <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{ stopColor: 'skyblue', stopOpacity: 1 }} />
                    <stop offset="100%" style={{ stopColor: 'purple', stopOpacity: 1 }} />
                </linearGradient>
            </defs>
            <RoomIcon sx={{ fill: 'url(#gradient1)' }} />
        </SvgIcon>

        <SvgIcon
            component="svg"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            sx={{ fontSize: 60 }}
        >
            <defs>
                <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{ stopColor: 'skyblue', stopOpacity: 1 }} />
                    <stop offset="100%" style={{ stopColor: 'purple', stopOpacity: 1 }} />
                </linearGradient>
            </defs>
            <MailIcon sx={{ fill: 'url(#gradient1)' }} />
        </SvgIcon>

        <SvgIcon
            component="svg"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            sx={{ fontSize: 60 }}
        >
            <defs>
                <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{ stopColor: 'skyblue', stopOpacity: 1 }} />
                    <stop offset="100%" style={{ stopColor: 'purple', stopOpacity: 1 }} />
                </linearGradient>
            </defs>
            <StickyNote2Icon sx={{ fill: 'url(#gradient1)' }} />
        </SvgIcon>
    </Box>
);

const OffersSection = ({ offers }) => (
    <SoftBox mt={2}>
        <SoftTypography variant="h6" sx={{ display: 'inline-block', fontWeight: 'bold', color: '#000000' }}>
            EXCLUSIVELY ON 1ONUS
        </SoftTypography>
        <>{console.log("fghd",offers)
        }</>
        {offers.map((offer, index) => (
           offer.status !== 'EXPIRED' &&
            <Card
                key={index}
                sx={{
                    mt: 2,
                    p: 2,
                    border: '2px solid transparent',
                    background: 'linear-gradient(white, white) padding-box, linear-gradient(90deg,#3491EB, #E68DF3) border-box',
                }}
            >
                <SoftBox display="flex" alignItems="center">
                    <SoftBox
                        p={0}
                        display="flex"
                        alignItems="center"
                    >
                        {offer?.type == "Buy One Get One"
                            ? <img src={require("../../../../assets/images/bogo.png")} alt="exclusive" width={50} height={60} />
                            : <img src={require("../../../../assets/images/offer.png")} alt="exclusive" width={50} height={60} />
                        }
                    </SoftBox>
                    <SoftBox
                        display="flex"
                        flexDirection="column"
                        ml={2}
                    >
                        <SoftTypography variant="h6" fontWeight={800} sx={{ display: 'inline-block', color: '#000000' }}>
                            {offer?.name}
                        </SoftTypography>
                        <SoftTypography variant="caption" sx={{ display: 'inline-block', color: 'gray' }}>
                            {offer?.description}
                        </SoftTypography>
                    </SoftBox>
                </SoftBox>
            </Card>
        ))}
    </SoftBox>
);

const AboutSection = ({ description }) => (
    <SoftBox mt={2}>
        <SoftTypography
            variant="h6"
            sx={{ textAlign: 'center' }}
        >
            About
        </SoftTypography>

        <SoftBox mt={1}>
            <SoftTypography
                variant="caption"
                sx={{
                    display: 'block',
                    textAlign: 'center',
                    color: 'gray',
                }}
                dangerouslySetInnerHTML={{ __html: description }}
            />
        </SoftBox>
    </SoftBox>

);
const HighLightSection = ({ highlights }) => {
    if (!highlights || highlights.length === 0) return null;

    return (
        <SoftBox mt={3}>
            <SoftTypography
                variant="h6"
                sx={{ textAlign: 'center' }}
            >
                Highlights
            </SoftTypography>
            {highlights.map((highlight, index) => (
                <SoftBox key={highlight._id || index} sx={{ mt: index === 0 ? 2 : 1 }}>
                    <SoftTypography
                        variant="h6"
                        sx={{ 
                            textAlign: 'start',
                            color: 'gray',
                            fontSize: '0.9rem',
                            fontWeight: 'bold'
                        }}
                    >
                        {highlight.title}
                    </SoftTypography>
                    <SoftBox sx={{ mt: '5px' }}>
                        <SoftTypography
                            variant="caption"
                            sx={{
                                display: 'block',
                                color: 'gray',
                            }}
                            dangerouslySetInnerHTML={{ __html: highlight.description }}
                        />
                    </SoftBox>
                </SoftBox>
            ))}
        </SoftBox>
    );
};
const OperatingHours = ({ hours }) => (
    <SoftBox
        mt={2}
        sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'start',
            flexDirection: 'column',
        }}
    >
        {/* <SoftTypography
            variant="h6"
        >
            About
        </SoftTypography>
        <SoftBox mt={3}>
            <SoftTypography variant="caption" sx={{ display: 'inline-block', textAlign: "center", color: "gray" }}>
                If you are a manager and would like to make edits to your listing, please
            </SoftTypography>
        </SoftBox> */}
        {hours.length > 0 && (
            <Accordion
                sx={{
                    background: 'none',
                    boxShadow: 'none',
                    width: "100%",
                    padding: 0,
                }}

            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                    sx={{ width: "100%", padding: 0, }}
                >
                    <SoftBox
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: "100%",
                        }}
                    >
                        <Box
                            sx={{
                                background: '#F8E0F9',
                                borderRadius: '50%',
                                padding: '10px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                color: '#333',
                            }}
                        >
                            <SvgIcon
                                component="svg"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                sx={{ fontSize: 50 }}
                            >
                                <defs>
                                    <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                                        <stop offset="0%" style={{ stopColor: 'skyblue', stopOpacity: 1 }} />
                                        <stop offset="100%" style={{ stopColor: 'purple', stopOpacity: 1 }} />
                                    </linearGradient>
                                </defs>
                                <LockClock sx={{ fill: 'url(#gradient1)' }} />
                            </SvgIcon>
                        </Box>
                        <SoftBox ml={2} mt={0}>
                            <SoftTypography fontSize="14px" fontWeight="normal"
                            >
                                Operating Hours
                            </SoftTypography>
                        </SoftBox>
                    </SoftBox>
                </AccordionSummary>
                <AccordionDetails
                    sx={{
                        padding: 0,
                        paddingLeft: 1,
                    }}
                >
                    <Box sx={{ flexGrow: 1, width: 310 }} />
                    <SoftBox
                        sx={{
                            width: 230,
                        }}
                    >
                        {hours.map((operatingHour, index) => {
                            return (
                                <SoftBox key={index} display="flex" justifyContent="space-between" gap={5}>
                                    <SoftTypography
                                        sx={{
                                            mt: 1,
                                            fontSize: 14,
                                            color: 'gray'
                                        }}
                                        variant="caption"  >
                                        {operatingHour?.day}
                                    </SoftTypography>
                                    <SoftTypography
                                        sx={{
                                            mt: 1,
                                            color: 'gray'
                                        }}
                                        variant="caption"  >
                                        {operatingHour?.openingHours ? (operatingHour?.openingHours + '-' + operatingHour?.closingHours) : "Closed"}
                                        <br />
                                        {
                                            operatingHour?.secondOpeningHours && (operatingHour?.secondOpeningHours + "-" + operatingHour?.secondClosingHours)
                                        }
                                    </SoftTypography>
                                </SoftBox>
                            )
                        })}
                    </SoftBox>
                </AccordionDetails>
            </Accordion>
        )}
    </SoftBox>
);

const TermsAndConditions = ({ terms }) => (
    <SoftBox
        sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'start',
            flexDirection: 'column',
        }}
    >
        {terms && (
            <Accordion
                sx={{
                    background: 'none',
                    boxShadow: 'none',
                    padding: 0,
                    width: "100%",
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                    sx={{ padding: 0, width: "100%", }}
                >
                    <SoftBox
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: "100%",
                        }}
                    >
                        <Box
                            sx={{
                                background: '#F8E0F9',
                                borderRadius: '50%',
                                padding: '10px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                color: '#333',
                            }}
                        >
                            <SvgIcon
                                component="svg"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                sx={{ fontSize: 50 }}
                            >
                                <defs>
                                    <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                                        <stop offset="0%" style={{ stopColor: 'skyblue', stopOpacity: 1 }} />
                                        <stop offset="100%" style={{ stopColor: 'purple', stopOpacity: 1 }} />
                                    </linearGradient>
                                </defs>
                                <BookOnlineOutlined sx={{ fill: 'url(#gradient1)' }} />
                            </SvgIcon>
                        </Box>
                        <SoftBox
                            sx={{
                                ml: 2,
                                flexGrow: 1,
                            }}
                        >
                            <SoftTypography fontSize="14px" fontWeight="normal"
                            >
                                Terms and Conditions
                            </SoftTypography>
                        </SoftBox>
                    </SoftBox>
                </AccordionSummary>
                <AccordionDetails
                    sx={{
                        paddingLeft: 2,
                    }}>
                    <Box sx={{ flexGrow: 1, width: 310 }} />
                    <SoftTypography
                        variant="caption"
                        sx={{ color: 'gray' }}
                        dangerouslySetInnerHTML={{ __html: terms }} />
                </AccordionDetails>
            </Accordion>
        )}
    </SoftBox>
);

const AmenitiesSection = ({ amenities }) => (
    <SoftBox
        sx={{
            width: "100%",
            height: 'auto',
            mx: 'auto',
            background: 'none',
        }}
    >
        <SoftTypography
            variant="h6"
        >
            Amenities
        </SoftTypography>
        <SoftBox mt={2} display="flex" flexWrap="wrap" gap={1} >
            {amenities.map((amenity, index) => (
                <SoftBox
                    key={index}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    sx={{
                        width: '60px',
                        textAlign: 'center',
                    }}
                >
                    <Box
                        sx={{
                            ...styles.amenityIcon,
                        }}
                    >
                        <img
                            src={amenity?.logo}
                            alt={amenity?.name}
                            style={{ width: '32px', height: '32px' }}
                        />
                    </Box>
                    <SoftTypography variant="caption" mt={1}
                        sx={{ color: 'gray' }}
                    >
                        {amenity?.name}
                    </SoftTypography>
                </SoftBox>
            ))}
        </SoftBox>
    </SoftBox>
);

const Footer = () => (
    <SoftBox mt={3}>
        <SoftTypography variant="caption" sx={{ display: 'inline-block', textAlign: "center", color: "gray" }}>
            If you are a manager and would like to make edits to your listing, please <u style={{ color: "#c21fad" }}>Click here</u> to contact us.
        </SoftTypography>
    </SoftBox>
);

const Profile = ({ data }) => {
    const [offerData, setOfferData] = useState([]);
    const [highlightsData, setHighlightsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const id = localStorage.getItem("id");
    const userTheme = useSelector((state) => state.commonSlice.theme);
    console.log(id, 'id');

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${baseURL}get/all/offers/business/${id}`);
            const result = await response.json();
            if (result.data && result.data.length > 0) {
                const filterOffers = result.data
                    .filter(offer => offer && offer.businessLocations && Array.isArray(offer.businessLocations) &&
                        offer.businessLocations.some(business => business === data?._id))
                // .reverse();
                console.log(filterOffers, 'filterOffers');
                setOfferData(filterOffers);
            }
        } catch (err) {
            toast.error(err.message || 'Something went wrong');
        } finally {
            setLoading(false);
        }
    };

    const fetchHighlights = async () => {
        try {
            const response = await fetch(`${baseURL}getAllBusinessHighlights/${data?._id}`);
            const result = await response.json();
            if (result.success && result.data && result.data.length > 0) {
                setHighlightsData(result.data);
            }
        } catch (err) {
            console.error('Error fetching highlights:', err);
        }
    };

    useEffect(() => {
        if (data) {
            fetchData(data);
            fetchHighlights();
        } else {
            setLoading(false);
        }
    }, [data]);

    return (
        <Box sx={styles.container}>
            <Box sx={styles.contentWrapper}>
                <Box sx={styles.scrollableContent}>
                    {loading ? (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            background: 'linear-gradient(to bottom, #fef4ff, #ffffff)'
                        }}>
                            <Loading />
                        </Box>
                    ) : (
                        <>
                            <HeaderImage image={data?.images?.[0]?.url} />

                            <SoftBox sx={{ background: 'linear-gradient(to bottom, #fef4ff, #ffffff)', borderRadius: '16px' }}>
                                <Box sx={{ padding: 2, position: "relative" }}>
                                    <Card sx={{
                                        ...styles.mainCard,
                                        position: 'absolute',
                                        top: '-40px',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        width: '92%',
                                        zIndex: 1,
                                        paddingBottom: "0px",
                                    }}>
                                        <CardContent className='card-content'>
                                            <BusinessInfo
                                                businessName={data?.businessName}
                                                rating="5.0"
                                                keywords={data?.keywords}
                                            />

                                            {/* Keywords */}
                                            <SoftBox sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', height: 'auto' }} mt={1}>
                                                {data?.keywords?.map((keyword, index) => (
                                                    <SoftTypography key={index} variant="caption" sx={{ display: 'inline-block', color: 'gray' }}>
                                                        {keyword?.name !== "undefined" && (keyword?.name + (index === data?.keywords.length - 1 ? ' ' : ",\u00A0"))}
                                                    </SoftTypography>
                                                ))}
                                            </SoftBox>

                                            <SoftBox sx={styles.divider} mt={1} />

                                            {/* Contact Icons */}
                                            <ContactIcons />
                                        </CardContent>
                                    </Card>
                                    <Box sx={{ paddingTop: "70px" }}></Box>
                                    {/* Offers Section */}
                                    {offerData.length > 0 && (
                                        <OffersSection offers={offerData} />
                                    )}

                                    {/* Highlights Section */}
                                    {highlightsData.length > 0 && (
                                        <HighLightSection highlights={highlightsData} />
                                    )}
                                    {data?.description && (
                                        <AboutSection description={data.description} />
                                    )}

                                    {/* Operating Hours */}
                                    {data?.operatingHours?.length > 0 && (
                                        <OperatingHours hours={data.operatingHours} />
                                    )}

                                    {/* Terms and Conditions */}
                                    {data?.termsAndConditions && (
                                        <TermsAndConditions terms={data.termsAndConditions} />
                                    )}

                                    {/* Amenities */}
                                    {data?.amenities?.length > 0 && (
                                        <AmenitiesSection amenities={data.amenities} />
                                    )}

                                    {/* Footer */}
                                    <Footer />
                                </Box>
                            </SoftBox>
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default Profile;


